import React, { useCallback, useEffect } from "react";
import { IconButton, Popover, Tooltip, Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button, Typography, CircularProgress, Icon, Container, Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Add, PostAdd} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { authHeader, getLocalStorageItem, setLocalStorageItem } from "../../_helpers";
import RichTextEditor from "./RichTextEditor";
import { flushSync } from 'react-dom';
import DataGridV2 from "../../components/DataGridV2";
import { LOCAL_STORAGE_GRID_KEYS } from "../../_constants";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { INITIAL_ROW_SELECTION_MODEL } from "../../components/DataGridV2/constants";
import { useMemo } from "react";
import { getStandardTextManagerColumns } from "./standardTextManagerUtils";

const { webApiUrl } = window.appConfig;

const useStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
  },
  grid: {
    maxWidth: 980,
    padding: theme.spacing(0, 1, 1),
    [theme.breakpoints.down("lg")]: {
      maxWidth: "100%",
    },
    "& .MuiToolbar-root": {
      margin: theme.spacing(0, -2, -0.875),
    },
    "& .MuiTableRow-root": {
      verticalAlign: "top",
    },
    "& .MuiTableCell-root": {
      padding: theme.spacing(1),
      "&.MuiTableCell-head": {
        fontWeight: "bold",
      },
    },
  },
  iconButton: {
    //margin: theme.spacing(-1, 0)
  },
   mainContainer: {
    height: "100%",
    width: "100%",
    overflow: "auto",
    padding: 0,
  },
  markdownBlock: {
    display: "-webkit-box",
    maxWidth: 400,
    maxHeight: 40,
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

export default function StandardTextManager(props) {
  const { disabled, onSelect, onChange, projectId, size, ...rest } = props;
  const { t , i18n} = useTranslation();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [color, setColor] = React.useState("default");
  const [standardTexts, setStandardTexts] = React.useState(null);
  const [popupBlock, setPopupBlock] = React.useState(null);
  const apiRef = useGridApiRef();
  const keyPrefix =  "GENERAL";
  const [paginationModel, setPaginationModel] = React.useState(() => getLocalStorageItem(
    LOCAL_STORAGE_GRID_KEYS.STANDARD_TEXT_MANAGER_PAGINATION[keyPrefix], undefined
  ))
  const [rowSelectionModel, setRowSelectionModel] = React.useState(INITIAL_ROW_SELECTION_MODEL); 

  useEffect(() => {
    if(projectId){
      UpdateStandardTexts();
    }
  }, [projectId]);

  useEffect(() => {
    onChange(standardTexts);
  }, [standardTexts]);

  function UpdateStandardTexts() {
    if (projectId) {
      const url = webApiUrl + "api/chat/GetStandardTextsByProject/" + encodeURI(projectId);
      const requestOptions = {
        method: "GET",
        headers: authHeader(),
      };

      fetch(url, requestOptions)
        .then((Response) => Response.json())
        .then((findResponse) => {
          setStandardTexts(findResponse);
        })
        .catch((e) => {
          setTimeout(UpdateStandardTexts, 1000);
        });
    }
  }

  const handleClickAdd = (isSignature) => {
    setOpenDialog(true);
    let tempObj = {
      hashtag: "#",
      textBlock: "",
      isSignature,
    };

    if (isSignature === true) {
      const textToEdit = standardTexts?.find((x) => x.isSignature === true);
      if (textToEdit) {
        tempObj = {
          ...textToEdit,
          hashtag: "#" + textToEdit.hashtag,
        };
      } else {
        tempObj.hashtag = "#handtekening";
      }
    }

    setPopupBlock(tempObj);
  };

  const handleCloseAdd = () => {
    setOpenDialog(false);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setColor("primary");
  };

  const handleClose = () => {
    setAnchorEl(null);
    setColor("default");
  };

  const handleClickSave = () => {
    const texts = standardTexts.slice();
    if (!!popupBlock.textId) {
      const textToUpdate = texts.find((x) => x.textId === popupBlock.textId);
      if (textToUpdate) {
        const tag = popupBlock.hashtag.substr(1);
        if (textToUpdate.hashtag !== tag || textToUpdate.textBlock !== popupBlock.textBlock) {
          const url = webApiUrl + "api/chat/UpdateStandardText";
          const requestOptions = {
            method: "PATCH",
            headers: authHeader("application/json"),
            body: JSON.stringify({
              textId: popupBlock.textId,
              hashtag: tag,
              textBlock: popupBlock.textBlock,
              isSignature: popupBlock.isSignature,
            }),
          };

          fetch(url, requestOptions)
            .then((Response) => Response.json())
            .then((success) => {
              UpdateStandardTexts();
              setPopupBlock(null);
              setOpenDialog(false);
            })
            .catch((e) => {
              //this.setState({ uploading: false });
              alert(t("general.api.error"));
            });
        } else {
          setPopupBlock(null);
          setOpenDialog(false);
        }
      }
    } else if (projectId) {
      if (popupBlock.hashtag !== "#" && !standardTexts.find((x) => "#" + x.hashtag == popupBlock.hashtag)) {
        const url = webApiUrl + "api/chat/AddStandardTextForProject";
        const requestOptions = {
          method: "POST",
          headers: authHeader("application/json"),
          body: JSON.stringify({
            projectId: projectId,
            hashtag: popupBlock.hashtag.substr(1),
            textBlock: popupBlock.textBlock,
            isSignature: popupBlock.isSignature,
          }),
        };

        fetch(url, requestOptions)
          .then((Response) => Response.json())
          .then((success) => {
            UpdateStandardTexts();
            setPopupBlock(null);
            setOpenDialog(false);
          })
          .catch((e) => {
            //this.setState({ uploading: false });
            alert(t("general.api.error"));
          });
      }
    }
  };

  const handleEdit = (textId) => {
    setOpenDialog(true);
    const textToEdit = standardTexts.find((x) => x.textId === textId);
    setPopupBlock({
      ...textToEdit,
      hashtag: "#" + textToEdit.hashtag,
    });
  };

  const handleChangeHashtag = (value) => {
    if (value.match(/^#[A-Za-z0-9\-\.\_]*$/g) && value.length <= 41) {
      let error = false;
      const texts = standardTexts.slice();
      if (value === "#" || texts.find((x) => x.textId !== popupBlock.textId && "#" + x.hashtag === value)) {
        error = true;
      }
      setPopupBlock({
        ...popupBlock,
        hashtag: value,
        hashtagError: error,
      });
    }
  };
  const handleChangeTextBlock = (value) => {
    flushSync(()=>{
    setPopupBlock({
      ...popupBlock,
      textBlock: value,
      textBlockError: !value || value.trim() === "",
    });
    })
  };

  const handleDelete = (textId) => {
    const url = webApiUrl + "api/chat/DeleteStandardText/" + encodeURI(textId);
    const requestOptions = {
      method: "DELETE",
      headers: authHeader(),
    };

    fetch(url, requestOptions)
      .then((Response) => Response.json())
      .then((success) => {
        UpdateStandardTexts();
      })
      .catch((e) => {
        //this.setState({ uploading: false });
        alert(t("general.api.error"));
      });
  };
  
  const onPaginationModelChange = useCallback((newPaginationModel) => {
    setPaginationModel(newPaginationModel)
    setLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.STANDARD_TEXT_MANAGER_PAGINATION[keyPrefix],
      newPaginationModel
    );
  }, [keyPrefix])

  const rows = standardTexts?.filter((x) => !x.isSignature).map(row => ({ ...row, id: row.textId }))
   
  const open = Boolean(anchorEl);
  const id = open ? "emoji-popover" : undefined;
   const columns = getStandardTextManagerColumns({t,classes,keyPrefix,i18n,apiRef,handleDelete,handleEdit,onSelect,handleClose})

    const getToolbarProps = useMemo(() => {
       if (!standardTexts) return {};
      return {
        toolbarStartActions: [
          {
            title: t("general.add"),
            icon: Add,
           onClick :() => handleClickAdd(false)
          },
          {
            title: t("general.signature"),
            icon: () => <Icon className="fas fa-signature" color="primary" fontSize="small" />,
           onClick :() => handleClickAdd(true)
          }
        ],
      };
    }, [standardTexts, t]);

  const getStandardTextManagerPrintAndDownload = ({ isAllSelected = true, idsToExport = [] }) => {
    const userSelectedRows = isAllSelected ? rows : idsToExport.map(id => rows?.find(item => item?.id === id));    
   const results = userSelectedRows?.map(item => {
       const result = {};
       Object.keys(item).forEach(key => {
         let value = item[key];
   
         if (key === 'hashtag') {
           value = `#${value}`;
         }
         result[key] = value;
       });
   
       return result;
     });
     return results;
  };


  return (
    <React.Fragment>
      <Tooltip title={t("chat.standardTexts")}>
        <IconButton aria-describedby={id} disabled={disabled} onClick={handleClick} color={color} {...rest} size={size}>
          <PostAdd />
        </IconButton>
      </Tooltip>
      <Popover
        id={id}
        open={open && disabled !== true}
        className={classes.popover}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
         {
          standardTexts ?
            <Container maxWidth={false} className={classes.mainContainer}>
              <Box display="flex" flexDirection="column" sx={{ height: "100%" }}>
                <Box p={1}>
                  <Typography variant='h6'>{t("chat.standardTexts")}</Typography>
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <DataGridV2
                    disableRowSelectionOnClick 
                    localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.STANDARD_TEXT_MANAGER_SEARCH_TEXT[keyPrefix]}
                    localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS.STANDARD_TEXT_MANAGER_COLUMN_FILTER[keyPrefix]}
                    localColumnSortIdentifier={LOCAL_STORAGE_GRID_KEYS.STANDARD_TEXT_MANAGER_COLUMN_SORT[keyPrefix]}
                    localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.STANDARD_TEXT_MANAGER_COLUMN_ORDER[keyPrefix]}
                    localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.STANDARD_TEXT_MANAGER_COLUMN_WIDTH[keyPrefix]}
                    localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.STANDARD_TEXT_MANAGER_COLUMN_VISIBILITY[keyPrefix]}
                    apiRef={apiRef}
                    rows={rows}
                    forceMobileView={true}
                    columns={columns}
                    pagination
                    {...(!!paginationModel && { paginationModel })}
                    onPaginationModelChange={onPaginationModelChange}
                    toolbarProps={{
                      ...getToolbarProps,
                      title: t("chat.standardTexts"),
                      selectedRows: rowSelectionModel,
                      getData: getStandardTextManagerPrintAndDownload,
                    }}
                    filterMode="client"
                    onRowSelectionModelChange={(newRowSelectionModel) => {
                      setRowSelectionModel(newRowSelectionModel);
                    }}
                  />
                </Box>
              </Box>
            </Container>
            : <CircularProgress color="primary" size={24} />}

         <Dialog open={openDialog} onClose={handleCloseAdd} aria-labelledby="form-dialog-title" maxWidth="md" fullWidth>
              {popupBlock && (
                <React.Fragment>
                  <DialogTitle id="form-dialog-title">
                    {!!popupBlock.textId ? (popupBlock.isSignature ? t("chat.standardText.changeSignature") : t("chat.standardText.changeStandardText")) : popupBlock.isSignature ? t("general.signature") : t("chat.addStandardText")}
                  </DialogTitle>
                  <DialogContent>
                    {
                      //<DialogContentText>
                      //    To subscribe to this website, please enter your email address here. We will send updates
                      //    occasionally.
                      //</DialogContentText>
                    }
                    {
                      <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Hashtag *"
                        type="text"
                        value={popupBlock.hashtag}
                        maxLength={10}
                        onChange={(e) => handleChangeHashtag(e.target.value)}
                        fullWidth
                        error={popupBlock.hashtagError === true}
                        helperText={popupBlock.hashtagError === true && popupBlock.hashtag !== "#" && t("chat.standardText.hashtagAlreadyExistsInfo", { hashtag: popupBlock.hashtag })}
                      />
                    }
                    <br />
                    <br />
                    <Typography variant="caption">{t("chat.standardText.text.label")}&nbsp;*</Typography>
                    <RichTextEditor label={`${t("chat.standardText.text.label")}...`} showToolbar={true} onChange={(data) => handleChangeTextBlock(data)} value={popupBlock.textBlock} />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleCloseAdd} color="secondary">
                      {t("general.cancel")}
                    </Button>
                    <Button onClick={handleClickSave} color="primary" disabled={popupBlock.hashtagError === true || popupBlock.textBlockError === true}>
                      {t("general.save")}
                    </Button>
                  </DialogActions>
                </React.Fragment>
              )}
            </Dialog>

      </Popover>
    </React.Fragment>
  );
}
